import { create } from 'zustand'
import { ApiError, api, isApiError } from '@/api'
import type { ApiClient, Organization, OrgRole, StaffSession, StaffUser } from '@/api'
import { can } from './permissions'
import type { Permission } from './permissions'

/**
 * Who is signed in, and what they are allowed to see.
 *
 * The tokens are not here. They live inside the API client, which is the only
 * thing that needs to read them, and this store holds the answer to a different
 * question: whose session is this, which organization does it belong to, and
 * may this person open this screen. Keeping the two apart means a component
 * that subscribes to the signed-in user does not re-render every time a token
 * is refreshed in the background.
 *
 * There is no session restore, and that follows directly from the client
 * holding its tokens in memory: after a reload there is nothing to restore
 * from, so the initial status is `anonymous` rather than an `unknown` that
 * resolves later. That is a real cost, since a refresh sends somebody back to
 * the login screen, and it is the deliberate half of the trade written up in
 * `api/client.ts`.
 * The moment the server sets an httpOnly refresh cookie, this grows a
 * `restoring` status and a call on mount, and nothing else here changes.
 */

export type AuthStatus = 'anonymous' | 'authenticating' | 'authenticated'

interface AuthState {
  status: AuthStatus
  user: StaffUser | null
  organization: Organization | null
  /**
   * The last sign-in failure, for the login form to render.
   *
   * Cleared when a new attempt starts rather than on a timer, so the message
   * cannot outlive the form it belongs to.
   */
  error: ApiError | null

  signIn(email: string, password: string): Promise<void>
  acceptInvite(input: { token: string; name: string; password: string }): Promise<void>
  resetPassword(input: { token: string; password: string }): Promise<void>
  signOut(): Promise<void>
  /** Whether the signed-in role holds a permission. False when nobody is. */
  can(permission: Permission): boolean
}

export const useAuthStore = create<AuthState>()((setState, getState) => {
  /** One place that turns a grant into a signed-in session. */
  async function adopt(attempt: Promise<StaffSession>): Promise<void> {
    setState({ status: 'authenticating', error: null })
    try {
      const session = await attempt

      /*
       * A session is only a session if somebody is in it.
       *
       * Without this check a response missing `user` or `organization` is
       * adopted as valid, and the failure is quiet and strange: the shell
       * renders, the sidebar draws, and every permission check answers false
       * because there is no role to check against. The person sees a console
       * belonging to nobody, in which nothing works and nothing says why.
       *
       * That is not a hypothetical shape. It is what a version-skewed server
       * returns after a deploy that renames a field, which is exactly the
       * moment nobody is looking for a client-side bug.
       */
      if (!session.user || !session.organization) {
        throw new ApiError({
          status: 502,
          code: 'internal',
          message: 'Signing in did not work. Please try again.',
          recovery: 'retry',
        })
      }

      api.client.adopt(session)
      setState({
        status: 'authenticated',
        user: session.user,
        organization: session.organization,
        error: null,
      })
    } catch (error) {
      setState({
        status: 'anonymous',
        user: null,
        organization: null,
        error: isApiError(error) ? error : null,
      })
      throw error
    }
  }

  return {
    status: 'anonymous',
    user: null,
    organization: null,
    error: null,

    signIn(email, password) {
      return adopt(api.auth.login({ email, password }))
    },

    acceptInvite(input) {
      return adopt(api.auth.acceptInvite(input))
    },

    resetPassword(input) {
      return adopt(api.auth.resetPassword(input))
    },

    async signOut() {
      /*
       * The screen changes before the request finishes, and on purpose. The
       * client drops both tokens synchronously inside `signOut`, so by the time
       * this line runs there is nothing left to act with; awaiting the server's
       * acknowledgement first would leave the person looking at a signed-in
       * console for as long as a bad connection takes to give up.
       */
      const revoked = api.client.signOut()
      setState({ status: 'anonymous', user: null, organization: null, error: null })
      await revoked
    },

    can(permission) {
      return can(getState().user?.role ?? null, permission)
    },
  }
})

/**
 * Connects the client's lost-session signal to this store.
 *
 * Called from `App`, not at import time, and the direction is what makes it
 * worth a function: the client knows tokens and knows nothing about React, and
 * the store knows the user. Constructing either one with a reference to the
 * other would make them impossible to test apart, and doing it as a side effect
 * of importing this module would make the order of two imports load-bearing.
 *
 * Returns its own unsubscribe, so `useEffect` can hand it straight back and
 * StrictMode's double mount does not leave two listeners attached.
 */
export function watchSession(client: ApiClient): () => void {
  return client.onAuthLost(() => {
    useAuthStore.setState({
      status: 'anonymous',
      user: null,
      organization: null,
      error: null,
    })
  })
}

/** The signed-in role, or null. Read by route guards and by the nav. */
export function useRole(): OrgRole | null {
  return useAuthStore((state) => state.user?.role ?? null)
}

/**
 * Whether the signed-in person holds a permission.
 *
 * A hook rather than a call to `useAuthStore.getState().can(...)`, because the
 * second form does not subscribe: a component using it would keep rendering a
 * publish button after the person's role was changed under them.
 */
export function usePermission(permission: Permission): boolean {
  return useAuthStore((state) => can(state.user?.role ?? null, permission))
}
